import { LeftOutlined } from "@ant-design/icons"
import { App, Button, Flex, Skeleton, Space, Typography } from "antd"
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"
import { type ConnectedProps, connect } from "react-redux"
import { Link } from "react-router-dom"

import { FixedContainer } from "../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { ROLE_LABEL_STATUSES } from "../../constants/role-labels.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { useNotifications } from "../../hooks/use-notifications.ts"
import type { RootState } from "../../store.ts"
import { GccOwnerLockOverlay } from "./components/GccOwnerLockOverlay.tsx"
import {
  PermissionsForm,
  type PermissionsFormRef
} from "./components/PermissionsForm.tsx"
import { PermissionsLabels } from "./components/PermissionsLabels.tsx"
import { TransferOwnershipModalController } from "./components/TransferOwnershipModalController.tsx"
import {
  createLabel,
  deleteLabel,
  fetchDefaultPermissions,
  fetchLabelById,
  fetchLabels,
  updateLabel
} from "./redux/permissions.thunks.ts.ts"
import type {
  PermissionFeatures,
  PermissionLabel,
  PermissionLabelFormData
} from "./types/permissions.types.ts"
import {
  isBlockingError,
  useProcessPermissions
} from "./use-process-permissions.tsx"

const { Text, Title } = Typography

function PermissionsSettings({
  userLabels,
  fetchLabels,
  updateLabel,
  fetchLabelById,
  createLabel,
  deleteLabel,
  fetchDefaultPermissions
}: ConnectedProps<typeof connector>) {
  const { processPermissions, modalNode: blockingModalNode } =
    useProcessPermissions()
  const notification = useNotifications()
  const { modal } = App.useApp()
  const formRef = useRef<PermissionsFormRef>(null)

  const [activeLabel, setActiveLabel] = useState<PermissionLabel | null>(null)
  const [permissions, setPermissions] = useState<PermissionFeatures | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isGccPreview, setIsGccPreview] = useState(false)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false)

  const isGccOwnerLabel =
    activeLabel?.is_system === true &&
    activeLabel?.code === ROLE_LABEL_STATUSES.OWNER
  const hasGccOwnerLabel = userLabels?.some(
    (l) => l?.code === ROLE_LABEL_STATUSES.OWNER
  )

  const toggleTransferModal = () => setIsTransferModalOpen((v) => !v)
  const toggleGccPreview = () => setIsGccPreview((v) => !v)

  const requestSetActiveLabel = useCallback(
    (label: PermissionLabel) => {
      if (!formRef.current?.getIsDirty()) {
        setActiveLabel(label)
        return
      }

      modal.confirm({
        title: "Unsaved changes",
        content:
          "You have unsaved changes in this label. Discard them and switch?",
        okText: "Discard",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => setActiveLabel(label)
      })
    },
    [modal]
  )

  const handleGetLabels = async () => {
    try {
      setLoading(true)
      const res = await fetchLabels().unwrap()
      const labels = res?.results || []
      const lastLabel = labels?.at(-1)

      if (lastLabel) setActiveLabel(lastLabel)

      return labels
    } catch {
      return []
    } finally {
      setLoading(false)
    }
  }

  const handleAddLabel = (data: PermissionLabelFormData) => {
    try {
      return createLabel({
        ...data,
        code: data?.name
          ?.toLowerCase()
          ?.replace(/[^\p{L}\p{N}]/gu, "")
          .replace(/^\p{N}+/u, "")
      }).unwrap()
    } catch (e) {
      console.error("Failed to create label:", e)
    }
  }

  const handleUpdateLabel = async (
    id: number,
    data: PermissionLabelFormData
  ) => {
    try {
      const updatedLabel = await updateLabel({
        id,
        data: {
          name: data.name,
          code: data?.code
        }
      }).unwrap()

      if (id === updatedLabel?.id) {
        setActiveLabel(updatedLabel)
      }

      return updatedLabel
    } catch (e) {
      console.error("Failed to update label:", e)
    }
  }

  const handleDeleteLabel = (id: number) => {
    return deleteLabel(id).unwrap()
  }

  const handleDuplicateLabel = async (item: PermissionLabel) => {
    try {
      if (
        item.id === activeLabel?.id &&
        !loading &&
        !isUpdating &&
        permissions
      ) {
        return await createLabel({
          ...item,
          permissions: permissions
        }).unwrap()
      } else {
        const res = await fetchLabelById(item.id).unwrap()
        if (res.permissions) {
          return await createLabel({
            ...item,
            permissions: res.permissions
          }).unwrap()
        }
      }
    } catch (e) {
      console.error("Failed to duplacete label:", e)
    }
  }

  const handleGetPermissions = async (id: number) => {
    try {
      setLoading(true)
      const res = await fetchLabelById(id).unwrap()
      setPermissions(res?.permissions)
    } finally {
      setLoading(false)
    }
  }

  const handleGetDefaultPermissions = useCallback(async () => {
    const id = activeLabel?.id
    if (!id) return
    return await fetchDefaultPermissions(id).unwrap()
  }, [fetchDefaultPermissions, activeLabel?.id])

  const handleFinish = useCallback(
    (values: PermissionFeatures) => {
      if (!activeLabel) return

      const submit = async (
        propagate: boolean,
        override = false
      ): Promise<void> => {
        try {
          setIsUpdating(true)
          const res = await updateLabel({
            id: activeLabel.id,
            data: {
              name: activeLabel.name,
              code: activeLabel.code,
              propagate,
              ...(override && { override }),
              permissions: values
            }
          }).unwrap()

          if (res?.permissions) {
            setPermissions(res.permissions)
          }

          notification.success({
            message: "Permissions updated",
            description: "Permissions have been updated successfully."
          })
        } catch (e: unknown) {
          if (isBlockingError(e)) {
            const resolved = await processPermissions(e.blocking)
            if (resolved) {
              await submit(propagate, override)
            }
          } else {
            console.error("Failed to update permissions:", e)
          }
        } finally {
          setIsUpdating(false)
        }
      }

      const instance: ReturnType<typeof modal.confirm> = modal.confirm({
        title: "Save permissions",
        content:
          "Choose how to apply the changes to users currently assigned this label.",
        icon: null,
        closable: true,
        footer: (
          <Flex vertical gap={8} className="mt-4">
            <Button
              className={"h-auto py-2 justify-start text-start"}
              block
              onClick={() => {
                instance.destroy()
                void submit(false)
              }}
            >
              <Flex vertical gap={1}>
                <span className="font-semibold">Save Role</span>
                <span className="text-xs font-normal opacity-60 whitespace-normal">
                  The role will be updated. Current users will keep their
                  existing permissions.
                </span>
              </Flex>
            </Button>
            <Button
              block
              className={"h-auto py-2 justify-start text-start"}
              type="primary"
              onClick={() => {
                instance.destroy()
                void submit(true)
              }}
            >
              <Flex vertical gap={1}>
                <span className="font-semibold">Update Assigned Users</span>
                <span className="text-xs font-normal opacity-70 whitespace-normal">
                  The role will be updated, and permission changes will apply to
                  users assigned to this role. Only changed permissions will be
                  updated.
                </span>
              </Flex>
            </Button>
            <Button
              block
              danger
              className={"h-auto py-2 justify-start text-start"}
              onClick={() => {
                instance.destroy()
                void submit(true, true)
              }}
            >
              <Flex vertical gap={1}>
                <span className="font-semibold">
                  Reset Users to Role Defaults
                </span>
                <span className="text-xs font-normal opacity-70 whitespace-normal">
                  The role will be updated, and all assigned users will be fully
                  synced with the role permissions. Custom user permissions will
                  be replaced.
                </span>
              </Flex>
            </Button>
          </Flex>
        )
      })
    },
    [activeLabel, updateLabel, modal, notification]
  )

  useEffect(() => {
    if (activeLabel?.id) {
      void handleGetPermissions(activeLabel?.id)
      setIsGccPreview(false)
    }
  }, [activeLabel?.id])

  useEffect(() => {
    generateDocumentTitle("Settings Permissions")
  }, [])

  return (
    <FixedContainer>
      {blockingModalNode}
      {isTransferModalOpen && (
        <Suspense>
          <TransferOwnershipModalController onClose={toggleTransferModal} />
        </Suspense>
      )}

      <Flex align="flex-start" justify="space-between">
        <Space direction="vertical" size={2}>
          <Space>
            <Link to={RoutesPaths.settings.profile}>
              <Button icon={<LeftOutlined />} size="small" />
            </Link>
            <Title level={2} className="m-0 text-start">
              Permissions
            </Title>
          </Space>
          <Text type="secondary">Configure what each role can see and do</Text>
        </Space>
      </Flex>

      <PermissionsLabels
        activeLabel={activeLabel}
        setActiveLabel={requestSetActiveLabel}
        getLabels={handleGetLabels}
        onAddLabel={handleAddLabel}
        onUpdateLabel={handleUpdateLabel}
        onDeleteLabel={handleDeleteLabel}
        onDuplicateLabel={handleDuplicateLabel}
      />

      {loading ? (
        <div className={"overflow-auto tailwind-scroll"}>
          <div className={"grid grid-cols-1 2xl:grid-cols-2 gap-3"}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton.Node key={i} className={"w-full h-[35vh]"} active />
            ))}
          </div>
        </div>
      ) : isGccOwnerLabel && !isGccPreview ? (
        <GccOwnerLockOverlay
          hideActions={!hasGccOwnerLabel}
          onTransferOwnership={toggleTransferModal}
          onViewPermissions={toggleGccPreview}
        />
      ) : (
        <PermissionsForm
          isSystem={activeLabel?.is_system}
          permissions={permissions}
          saving={isUpdating}
          disabled={isGccOwnerLabel}
          onTransferOwnership={isGccOwnerLabel ? toggleGccPreview : undefined}
          getDefaultPermissions={handleGetDefaultPermissions}
          formRef={formRef}
          onFinish={handleFinish}
        />
      )}
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({
  userLabels: state?.auth?.userInfo?.permissions?.labels
})

const mapDispatchToProps = {
  fetchLabels,
  fetchLabelById,
  updateLabel,
  createLabel,
  deleteLabel,
  fetchDefaultPermissions
}
const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(PermissionsSettings)
